import React, { useState, useEffect, useCallback } from 'react';
import { lifeGoalsApi } from '../api';
import { useAppBackHandler } from '../navigation/AppNavigation';

interface LifeGoal {
  id: number;
  title: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  icon: string;
  progress: number;
  target_date: string | null;
  time_frame: string;
  parent_goal_id?: number | null;
  needs_review?: boolean;
  last_reviewed_at?: string | null;
  milestones: { id: number; title: string; is_completed: boolean; order: number }[];
  milestone_progress: { total: number; completed: number };
  sub_goals?: LifeGoal[];
}

interface GoalReviewItem {
  id: number;
  progress_snapshot: number;
  achievements: string;
  obstacles: string;
  lessons: string;
  adjustments: string;
  created_at: string;
}

interface GoalGroup {
  key: string;
  label: string;
  icon: string;
  color: string;
  goals: LifeGoal[];
  count: number;
}

interface Template {
  key: string;
  title: string;
  icon: string;
  category: string;
  time_frame: string;
}

interface ParentChoice {
  id: number;
  title: string;
  icon: string;
}

interface Props {
  initialGoalId?: number;
  onClose: () => void;
}

const PRIORITY_LABEL: Record<string, string> = { high: '高', medium: '中', low: '低' };

const TF_META: Record<string, { label: string; icon: string; color: string }> = {
  life: { label: '人生目标', icon: '🌟', color: '#c084fc' },
  yearly: { label: '年度目标', icon: '📅', color: '#f59e0b' },
  quarterly: { label: '季度目标', icon: '🗓', color: '#60a5fa' },
  monthly: { label: '月度目标', icon: '📋', color: '#34d399' },
  weekly: { label: '周度目标', icon: '⚡', color: '#fb923c' },
};

const LONG_TF = ['life', 'yearly'];
const SHORT_TF = ['quarterly', 'monthly', 'weekly'];

// PDCA 四阶段定义
const PDCA_STEPS = [
  { key: 'plan', label: 'Plan', cn: '计划', icon: '📝' },
  { key: 'do', label: 'Do', cn: '执行', icon: '🚀' },
  { key: 'check', label: 'Check', cn: '回顾', icon: '🔍' },
  { key: 'act', label: 'Act', cn: '改进', icon: '♻️' },
];

const LifeGoalsCenter: React.FC<Props> = ({ onClose, initialGoalId }) => {
  const [loading, setLoading] = useState(true);
  const [purposeScore, setPurposeScore] = useState(0);
  const [components, setComponents] = useState<any[]>([]);
  const [groupedGoals, setGroupedGoals] = useState<GoalGroup[]>([]);
  const [reviewNeeded, setReviewNeeded] = useState<LifeGoal[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [parentChoices, setParentChoices] = useState<ParentChoice[]>([]);
  const [statusFilter, setStatusFilter] = useState('active');
  const [showComponents, setShowComponents] = useState(false);
  const [selectedGoalId, setSelectedGoalId] = useState<number | null>(initialGoalId || null);
  useEffect(() => {
    if (initialGoalId) setSelectedGoalId(initialGoalId);
  }, [initialGoalId]);
  const [goalDetail, setGoalDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeStep, setActiveStep] = useState<string>('plan');

  useAppBackHandler(() => {
    if (!selectedGoalId) return false;
    setSelectedGoalId(null);
    return true;
  });
  const [showCreate, setShowCreate] = useState(false);
  const [newGoal, setNewGoal] = useState({
    title: '', description: '', category: 'other', priority: 'medium', icon: '🎯',
    target_date: '', time_frame: 'life', milestones: '', parent_goal_id: '' as string,
  });
  const [showReview, setShowReview] = useState(false);
  const [reviewForm, setReviewForm] = useState({ progress: 0, achievements: '', obstacles: '', lessons: '', adjustments: '' });
  const [newMilestone, setNewMilestone] = useState('');

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const res = await lifeGoalsApi.getDashboard(statusFilter);
      const d = res.data;
      setPurposeScore(d.purpose_score ?? 0);
      setComponents(d.components || []);
      setGroupedGoals(d.grouped_goals || []);
      setReviewNeeded(d.review_needed || []);
      setTemplates(d.goal_templates || []);
      setParentChoices(d.parent_choices || []);
    } catch (e) {
      console.error('加载目标页失败', e);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const loadDetail = useCallback(async (id: number) => {
    try {
      setDetailLoading(true);
      const res = await lifeGoalsApi.getById(id);
      setGoalDetail(res.data);
      setReviewForm(f => ({ ...f, progress: res.data.progress ?? 0 }));
      // 进入详情时根据状态推断当前 PDCA 阶段
      setActiveStep(inferStep(res.data));
    } catch (e) {
      console.error(e);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);
  useEffect(() => {
    if (selectedGoalId) loadDetail(selectedGoalId);
    else setGoalDetail(null);
  }, [selectedGoalId, loadDetail]);

  // 推断目标当前所处的 PDCA 阶段
  const inferStep = (g: any): string => {
    const total = g.milestone_progress?.total ?? 0;
    const hasReview = (g.reviews?.length ?? 0) > 0;
    if (total === 0) return 'plan';            // 还没拆里程碑 → Plan
    if (g.needs_review) return 'check';        // 到期需回顾 → Check
    if (hasReview && g.reviews[0]?.adjustments) return 'act'; // 有回顾且有调整 → Act
    return 'do';                               // 正在执行 → Do
  };

  const longGroups = groupedGoals.filter(g => LONG_TF.includes(g.key));
  const shortGroups = groupedGoals.filter(g => SHORT_TF.includes(g.key));
  const longCount = longGroups.reduce((s, g) => s + g.count, 0);
  const shortCount = shortGroups.reduce((s, g) => s + g.count, 0);
  const longParents = parentChoices;

  const handleCreate = async () => {
    if (!newGoal.title.trim()) { alert('请输入标题'); return; }
    const milestones = newGoal.milestones.split('\n').map(s => s.trim()).filter(Boolean);
    await lifeGoalsApi.create({
      title: newGoal.title,
      description: newGoal.description,
      category: newGoal.category,
      priority: newGoal.priority,
      icon: newGoal.icon,
      target_date: newGoal.target_date || undefined,
      time_frame: newGoal.time_frame,
      parent_goal_id: newGoal.parent_goal_id ? Number(newGoal.parent_goal_id) : null,
      milestones,
    });
    setShowCreate(false);
    setNewGoal({ title: '', description: '', category: 'other', priority: 'medium', icon: '🎯', target_date: '', time_frame: 'life', milestones: '', parent_goal_id: '' });
    loadDashboard();
  };

  const openCreate = (tf: string) => {
    setNewGoal(p => ({ ...p, time_frame: tf, parent_goal_id: '' }));
    setShowCreate(true);
  };

  const handleTemplate = async (key: string) => {
    await lifeGoalsApi.createFromTemplate(key);
    loadDashboard();
  };

  const handleReviewSubmit = async () => {
    if (!selectedGoalId) return;
    await lifeGoalsApi.submitReview(selectedGoalId, reviewForm);
    setShowReview(false);
    setReviewForm({ progress: reviewForm.progress, achievements: '', obstacles: '', lessons: '', adjustments: '' });
    loadDetail(selectedGoalId);
    loadDashboard();
  };

  const renderGoalCard = (goal: LifeGoal, depth = 0) => {
    const subCount = goal.sub_goals?.length ?? 0;
    const tf = TF_META[goal.time_frame] || TF_META.life;
    return (
      <div
        key={goal.id}
        className="gl-goal-card"
        style={{ marginLeft: depth * 12, ['--card-color' as string]: tf.color }}
        onClick={() => setSelectedGoalId(goal.id)}
        onKeyDown={() => {}}
        role="button"
        tabIndex={0}
      >
        <div className="gl-goal-head">
          <span className="gl-goal-icon">{goal.icon}</span>
          <div className="gl-goal-info">
            <div className="gl-goal-title">{goal.title}</div>
            <div className="gl-goal-meta">
              <span className="gl-tf-chip" style={{ color: tf.color, borderColor: `${tf.color}55` }}>{tf.icon} {tf.label}</span>
              <span className={`gl-badge ${goal.priority}`}>{PRIORITY_LABEL[goal.priority] || goal.priority}</span>
              {goal.needs_review && <span className="gl-review-tag">需回顾</span>}
            </div>
          </div>
          <span className="gl-goal-pct">{goal.progress}%</span>
        </div>
        <div className="gl-goal-bar-track"><div className="gl-goal-bar-fill" style={{ width: `${goal.progress}%` }} /></div>
        {subCount > 0 && (
          <div className="gl-sub-strip">
            🔗 {subCount} 个短期目标支撑
            <span className="gl-sub-mini">
              {goal.sub_goals!.slice(0, 3).map(sg => (
                <i key={sg.id} title={sg.title}>{sg.icon}</i>
              ))}
            </span>
          </div>
        )}
      </div>
    );
  };

  // ===== PDCA 详情页 =====
  if (selectedGoalId && goalDetail) {
    const g = goalDetail;
    const tf = TF_META[g.time_frame] || TF_META.life;
    const reviews: GoalReviewItem[] = g.reviews || [];
    const latestReview = reviews[0];
    const stepIdx = PDCA_STEPS.findIndex(s => s.key === activeStep);

    return (
      <div className="dim-root">
        <header className="dim-header-bar">
          <button type="button" className="dim-back-btn" onClick={() => setSelectedGoalId(null)} aria-label="返回">←</button>
          <h1>{g.icon} {g.title}</h1>
          <span style={{ width: 36 }} />
        </header>
        <main className="dim-main">
          {detailLoading ? <div className="dim-loading"><div className="dim-spinner" /></div> : (
            <>
              {g.breadcrumbs?.length > 0 && (
                <div className="gl-bc">{g.breadcrumbs.map((b: any) => <span key={b.id}>{b.icon} {b.title}</span>)}<span className="gl-bc-cur">{g.icon} {g.title}</span></div>
              )}

              <div className="gl-detail-top">
                <span className="gl-tf-chip lg" style={{ color: tf.color, borderColor: `${tf.color}55` }}>{tf.icon} {tf.label}</span>
                <div className="gl-detail-pct" style={{ color: tf.color }}>{g.progress}%</div>
              </div>
              <div className="gl-goal-bar-track lg"><div className="gl-goal-bar-fill" style={{ width: `${g.progress}%` }} /></div>

              {/* PDCA 步骤条 */}
              <div className="gl-pdca-stepper">
                {PDCA_STEPS.map((s, i) => (
                  <button
                    key={s.key}
                    type="button"
                    className={`gl-pdca-step ${activeStep === s.key ? 'active' : ''} ${i < stepIdx ? 'passed' : ''}`}
                    onClick={() => setActiveStep(s.key)}
                  >
                    <span className="gl-pdca-dot">{s.icon}</span>
                    <span className="gl-pdca-lbl">{s.label}<em>{s.cn}</em></span>
                  </button>
                ))}
              </div>

              {/* Plan */}
              {activeStep === 'plan' && (
                <section className="dim-section gl-pdca-panel">
                  <h3>📝 Plan · 规划目标与里程碑</h3>
                  {g.description && <p className="gl-desc">{g.description}</p>}
                  <div className="gl-plan-meta">
                    <span>🏁 截止：{g.target_date || '未设定'}</span>
                    <span>⚑ 优先级：{PRIORITY_LABEL[g.priority] || g.priority}</span>
                  </div>
                  <h4 className="gl-sub-h">里程碑 ({g.milestone_progress?.completed}/{g.milestone_progress?.total})</h4>
                  {g.milestones?.map((m: any) => (
                    <div key={m.id} className="gl-ms">
                      <span onClick={() => lifeGoalsApi.toggleMilestone(g.id, m.id).then(() => loadDetail(g.id))}>
                        {m.is_completed ? '✅' : '⬜'} {m.title}
                      </span>
                      <button type="button" className="gl-ms-del" onClick={() => lifeGoalsApi.deleteMilestone(g.id, m.id).then(() => loadDetail(g.id))}>✕</button>
                    </div>
                  ))}
                  <div className="gl-ms-add">
                    <input value={newMilestone} onChange={e => setNewMilestone(e.target.value)} placeholder="拆解一个里程碑" />
                    <button type="button" onClick={() => {
                      if (!newMilestone.trim()) return;
                      lifeGoalsApi.addMilestone(g.id, newMilestone.trim()).then(() => { setNewMilestone(''); loadDetail(g.id); });
                    }}>+</button>
                  </div>
                  <button type="button" className="gl-step-next" onClick={() => setActiveStep('do')}>下一步：执行 →</button>
                </section>
              )}

              {/* Do */}
              {activeStep === 'do' && (
                <section className="dim-section gl-pdca-panel">
                  <h3>🚀 Do · 执行与打卡</h3>
                  <p className="gl-desc">勾选已完成的里程碑，进度自动更新；也可直接拖动进度。</p>
                  {g.milestones?.length > 0 ? g.milestones.map((m: any) => (
                    <div key={m.id} className="gl-ms" onClick={() => lifeGoalsApi.toggleMilestone(g.id, m.id).then(() => loadDetail(g.id))}>
                      {m.is_completed ? '✅' : '⬜'} {m.title}
                    </div>
                  )) : <p className="dim-empty">还没有里程碑，先回到 Plan 拆解</p>}
                  <label className="gl-slider-lbl">手动进度：{g.progress}%</label>
                  <input
                    type="range" min={0} max={100} value={g.progress}
                    onChange={e => setGoalDetail((d: any) => ({ ...d, progress: Number(e.target.value) }))}
                    onMouseUp={e => lifeGoalsApi.update(g.id, { progress: Number((e.target as HTMLInputElement).value) }).then(() => loadDetail(g.id))}
                    onTouchEnd={() => lifeGoalsApi.update(g.id, { progress: g.progress }).then(() => loadDetail(g.id))}
                  />
                  <button type="button" className="gl-step-next" onClick={() => setActiveStep('check')}>下一步：回顾 →</button>
                </section>
              )}

              {/* Check */}
              {activeStep === 'check' && (
                <section className="dim-section gl-pdca-panel">
                  <h3>🔍 Check · 定期回顾</h3>
                  {g.needs_review
                    ? <div className="gl-review-alert sm">🔔 该目标已到回顾周期，记录这一阶段的得失。</div>
                    : <p className="gl-desc">回顾你的成就、障碍与教训，沉淀经验。</p>}
                  <button type="button" className="dim-btn" onClick={() => { setReviewForm(f => ({ ...f, progress: g.progress })); setShowReview(true); }}>＋ 写一次回顾</button>
                  {reviews.length === 0 ? <p className="dim-empty">暂无回顾记录</p> : (
                    <div className="gl-timeline">
                      {reviews.map(r => (
                        <div key={r.id} className="gl-tl-item">
                          <div className="gl-tl-head"><b>{r.progress_snapshot}%</b><span>{(r.created_at || '').slice(0, 10)}</span></div>
                          {r.achievements && <p>✅ 成就：{r.achievements}</p>}
                          {r.obstacles && <p>⛔ 障碍：{r.obstacles}</p>}
                          {r.lessons && <p>💡 教训：{r.lessons}</p>}
                          {r.adjustments && <p>🔧 调整：{r.adjustments}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                  <button type="button" className="gl-step-next" onClick={() => setActiveStep('act')}>下一步：改进 →</button>
                </section>
              )}

              {/* Act */}
              {activeStep === 'act' && (
                <section className="dim-section gl-pdca-panel">
                  <h3>♻️ Act · 调整与迭代</h3>
                  {latestReview?.adjustments
                    ? <div className="gl-act-note">最近一次回顾的调整建议：<br /><b>{latestReview.adjustments}</b></div>
                    : <p className="gl-desc">根据回顾结论调整目标方向；如方向偏离，可拆出新的短期子目标。</p>}
                  <div className="gl-act-grid">
                    <button type="button" onClick={() => lifeGoalsApi.update(g.id, { priority: 'high' }).then(() => loadDetail(g.id))}>⚑ 提升优先级</button>
                    <button type="button" onClick={() => { setNewGoal(p => ({ ...p, time_frame: 'weekly', parent_goal_id: String(g.id) })); setShowCreate(true); }}>＋ 拆短期子目标</button>
                    <button type="button" onClick={() => lifeGoalsApi.update(g.id, { status: 'paused' }).then(() => loadDetail(g.id))}>⏸ 暂停</button>
                    <button type="button" className="ok" onClick={() => lifeGoalsApi.update(g.id, { status: 'completed' }).then(() => { setSelectedGoalId(null); loadDashboard(); })}>✓ 完成目标</button>
                  </div>
                  {g.sub_goals?.length > 0 && (
                    <>
                      <h4 className="gl-sub-h">支撑此目标的短期子目标</h4>
                      {g.sub_goals.map((sg: LifeGoal) => renderGoalCard(sg, 0))}
                    </>
                  )}
                  <button type="button" className="danger gl-del-btn" onClick={() => { if (window.confirm('删除该目标？')) lifeGoalsApi.delete(g.id).then(() => { setSelectedGoalId(null); loadDashboard(); }); }}>删除目标</button>
                </section>
              )}
            </>
          )}
        </main>
        {showReview && (
          <div className="gl-modal-overlay" onClick={e => e.target === e.currentTarget && setShowReview(false)}>
            <div className="gl-modal gl-modal-tall">
              <h3>🔍 目标回顾（Check）</h3>
              <label>当前进度 %</label>
              <input type="number" min={0} max={100} value={reviewForm.progress} onChange={e => setReviewForm(f => ({ ...f, progress: Number(e.target.value) }))} />
              <label>✅ 成就 / 进展</label><textarea rows={2} value={reviewForm.achievements} onChange={e => setReviewForm(f => ({ ...f, achievements: e.target.value }))} />
              <label>⛔ 遇到的障碍</label><textarea rows={2} value={reviewForm.obstacles} onChange={e => setReviewForm(f => ({ ...f, obstacles: e.target.value }))} />
              <label>💡 教训 / 收获</label><textarea rows={2} value={reviewForm.lessons} onChange={e => setReviewForm(f => ({ ...f, lessons: e.target.value }))} />
              <label>🔧 下一步调整（Act 依据）</label><textarea rows={2} value={reviewForm.adjustments} onChange={e => setReviewForm(f => ({ ...f, adjustments: e.target.value }))} />
              <button type="button" className="gl-submit" onClick={handleReviewSubmit}>提交回顾</button>
            </div>
          </div>
        )}
        <style>{`${listStyles}\n${detailStyles}`}</style>
      </div>
    );
  }

  const renderSection = (title: string, sub: string, groups: GoalGroup[], count: number, tfForCreate: string) => (
    <section className="gl-zone">
      <div className="gl-zone-hdr">
        <div>
          <div className="gl-zone-title">{title}</div>
          <div className="gl-zone-sub">{sub}</div>
        </div>
        <button type="button" className="gl-zone-add" onClick={() => openCreate(tfForCreate)}>＋</button>
      </div>
      {count === 0 ? (
        <p className="dim-empty">暂无{title}，点击右上角 ＋ 或用下方模板创建</p>
      ) : (
        groups.map(grp => grp.goals.length > 0 && (
          <div key={grp.key}>
            <div className="gl-panel-hdr" style={{ color: grp.color }}>{grp.icon} {grp.label} <span>{grp.count}</span></div>
            {grp.goals.map(goal => renderGoalCard(goal))}
          </div>
        ))
      )}
    </section>
  );

  return (
    <div className="dim-root">
      <header className="dim-header-bar">
        <button type="button" className="dim-back-btn" onClick={onClose} aria-label="返回">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
        <h1>🎯 志 · 人生方向</h1>
        <button type="button" className="dim-add-hdr" onClick={() => openCreate('life')}>+</button>
      </header>

      <main className="dim-main">
        {loading ? (
          <div className="dim-loading"><div className="dim-spinner" /><p>加载中...</p></div>
        ) : (
          <>
            <div className="dim-score-card">
              <div className="dim-score-big" style={{ color: '#f59e0b' }}>{purposeScore}</div>
              <div className="dim-score-label">志 · 人生方向分</div>
              <div className="gl-score-split">
                <span>🌟 长期 {longCount}</span>
                <span>⚡ 短期 {shortCount}</span>
              </div>
            </div>

            <section className="dim-section">
              <h3>
                📊 成分分解
                <button type="button" className="gl-toggle" onClick={() => setShowComponents(v => !v)}>{showComponents ? '收起' : '展开'}</button>
              </h3>
              {showComponents && (
                <div className="dim-bars">
                  {components.map(c => (
                    <div key={c.label} className="dim-bar-row">
                      <span className="dim-bar-lbl">{c.label}</span>
                      <div className="dim-bar-track"><div className="dim-bar-fill" style={{ width: `${Math.min(100, (c.value / c.max) * 100)}%`, background: c.color }} /></div>
                      <span className="dim-bar-val">{c.value}/{c.max}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {reviewNeeded.length > 0 && (
              <div className="gl-review-alert">
                <div className="gl-review-title">🔔 {reviewNeeded.length} 个目标需要回顾（PDCA · Check）</div>
                {reviewNeeded.map(g => (
                  <div key={g.id} className="gl-review-row">
                    <span>{g.icon} {g.title}</span>
                    <button type="button" onClick={() => setSelectedGoalId(g.id)}>去回顾</button>
                  </div>
                ))}
              </div>
            )}

            <div className="gl-status-bar">
              {['all', 'active', 'completed', 'paused'].map(s => (
                <button key={s} type="button" className={`gl-status-btn ${statusFilter === s ? 'active' : ''}`} onClick={() => setStatusFilter(s)}>
                  {{ all: '全部', active: '进行中', completed: '已完成', paused: '已暂停' }[s]}
                </button>
              ))}
            </div>

            {/* 长期愿景区 */}
            {renderSection('🌟 长期愿景', '人生 / 年度 · 你想成为谁', longGroups, longCount, 'life')}

            {/* 短期行动区 */}
            {renderSection('⚡ 短期行动', '季度 / 月度 / 周度 · 支撑长期愿景的具体步骤', shortGroups, shortCount, 'weekly')}

            {/* 模板快捷区 */}
            <section className="dim-section">
              <h3>⚡ 快速从模板创建</h3>
              <div className="gl-quick">
                {templates.map(t => (
                  <button key={t.key} type="button" className="gl-quick-btn" onClick={() => handleTemplate(t.key)}>{t.icon} {t.title}</button>
                ))}
              </div>
            </section>
          </>
        )}
      </main>

      {showCreate && (
        <div className="gl-modal-overlay" onClick={e => e.target === e.currentTarget && setShowCreate(false)}>
          <div className="gl-modal gl-modal-tall">
            <h3>创建目标</h3>
            <label>时间框架</label>
            <select value={newGoal.time_frame} onChange={e => setNewGoal(p => ({ ...p, time_frame: e.target.value, parent_goal_id: LONG_TF.includes(e.target.value) ? '' : p.parent_goal_id }))}>
              {Object.entries(TF_META).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
            </select>
            {SHORT_TF.includes(newGoal.time_frame) && longParents.length > 0 && (
              <>
                <label>支撑哪个长期目标？（可选）</label>
                <select value={newGoal.parent_goal_id} onChange={e => setNewGoal(p => ({ ...p, parent_goal_id: e.target.value }))}>
                  <option value="">不挂载</option>
                  {longParents.map(p => <option key={p.id} value={p.id}>{p.icon} {p.title}</option>)}
                </select>
              </>
            )}
            <input placeholder="标题 *" value={newGoal.title} onChange={e => setNewGoal(p => ({ ...p, title: e.target.value }))} />
            <textarea placeholder="描述" rows={2} value={newGoal.description} onChange={e => setNewGoal(p => ({ ...p, description: e.target.value }))} />
            <input type="date" value={newGoal.target_date} onChange={e => setNewGoal(p => ({ ...p, target_date: e.target.value }))} />
            <select value={newGoal.category} onChange={e => setNewGoal(p => ({ ...p, category: e.target.value }))}>
              <option value="career">事业</option><option value="health">健康</option><option value="relationship">关系</option>
              <option value="finance">财务</option><option value="learning">学习</option><option value="spiritual">精神</option><option value="other">其他</option>
            </select>
            <textarea placeholder="里程碑（每行一个，Plan 阶段）" rows={3} value={newGoal.milestones} onChange={e => setNewGoal(p => ({ ...p, milestones: e.target.value }))} />
            <button type="button" className="gl-submit" onClick={handleCreate}>创建</button>
          </div>
        </div>
      )}

      <style>{`${listStyles}\n${detailStyles}`}</style>
    </div>
  );
};

const listStyles = `
  .dim-root { min-height: 100vh; background: #030712; color: #f8fafc; padding-bottom: 40px; }
  .dim-header-bar { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; padding-top: calc(16px + var(--status-bar-height, 28px)); background: rgba(15,23,42,0.95); border-bottom: 1px solid rgba(255,255,255,0.06); position: sticky; top: 0; z-index: 10; }
  .dim-header-bar h1 { font-size: 17px; margin: 0; font-weight: 700; flex: 1; text-align: center; }
  .dim-back-btn, .dim-add-hdr { width: 36px; height: 36px; border-radius: 10px; border: 1px solid rgba(255,255,255,0.1); background: rgba(139,92,246,0.15); color: #fff; cursor: pointer; font-size: 18px; display: flex; align-items: center; justify-content: center; }
  .dim-main { padding: 16px; }
  .dim-loading, .dim-empty { text-align: center; padding: 32px; color: #94a3b8; }
  .dim-empty { padding: 18px; font-size: 12px; }
  .dim-spinner { width: 32px; height: 32px; border: 3px solid rgba(255,255,255,0.1); border-top-color: #8b5cf6; border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto 12px; }
  .dim-score-card { background: linear-gradient(180deg, rgba(30,41,59,.82), rgba(15,23,42,.78)); border: 1px solid rgba(148,163,184,0.12); border-radius: 20px; padding: 24px; text-align: center; margin-bottom: 16px; }
  .dim-score-big { font-size: 48px; font-weight: 900; }
  .dim-score-label { font-size: 12px; color: #94a3b8; }
  .gl-score-split { display: flex; justify-content: center; gap: 18px; margin-top: 12px; font-size: 12px; color: #cbd5e1; }
  .dim-section { margin-bottom: 16px; }
  .dim-section h3 { font-size: 15px; margin: 0 0 10px; display: flex; align-items: center; gap: 8px; }
  .gl-toggle { margin-left: auto; font-size: 11px; background: none; border: none; color: #94a3b8; cursor: pointer; }
  .dim-bars { background: rgba(17,24,39,0.7); border-radius: 14px; padding: 16px; display: flex; flex-direction: column; gap: 10px; }
  .dim-bar-row { display: flex; align-items: center; gap: 10px; }
  .dim-bar-lbl { font-size: 11px; color: #94a3b8; min-width: 56px; }
  .dim-bar-track { flex: 1; height: 6px; background: rgba(30,41,59,0.8); border-radius: 3px; overflow: hidden; }
  .dim-bar-fill { height: 100%; border-radius: 3px; }
  .dim-bar-val { font-size: 11px; font-weight: 700; min-width: 40px; text-align: right; }
  .gl-review-alert { background: rgba(251,191,36,0.08); border: 1px solid rgba(251,191,36,0.2); border-radius: 14px; padding: 14px; margin-bottom: 16px; }
  .gl-review-alert.sm { padding: 10px; margin-bottom: 12px; font-size: 12px; color: #fcd34d; }
  .gl-review-title { font-weight: 700; color: #fbbf24; margin-bottom: 8px; font-size: 13px; }
  .gl-review-row { display: flex; justify-content: space-between; align-items: center; padding: 6px 0; font-size: 12px; }
  .gl-review-row button { padding: 4px 10px; border-radius: 8px; border: 1px solid rgba(251,191,36,0.3); background: rgba(251,191,36,0.1); color: #fbbf24; cursor: pointer; font-size: 11px; }
  .gl-status-bar { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 16px; }
  .gl-status-btn { padding: 5px 12px; border-radius: 999px; font-size: 11px; font-weight: 600; background: rgba(30,41,59,0.6); color: #94a3b8; border: 1px solid rgba(255,255,255,0.06); cursor: pointer; }
  .gl-status-btn.active { background: rgba(139,92,246,0.2); color: #a78bfa; border-color: rgba(139,92,246,0.3); }
  .gl-zone { background: rgba(15,23,42,0.5); border: 1px solid rgba(255,255,255,0.05); border-radius: 18px; padding: 16px; margin-bottom: 16px; }
  .gl-zone-hdr { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
  .gl-zone-title { font-size: 16px; font-weight: 800; }
  .gl-zone-sub { font-size: 11px; color: #94a3b8; margin-top: 2px; }
  .gl-zone-add { width: 30px; height: 30px; border-radius: 9px; border: 1px solid rgba(139,92,246,0.3); background: rgba(139,92,246,0.15); color: #a78bfa; font-size: 18px; cursor: pointer; }
  .gl-quick { display: flex; flex-wrap: wrap; gap: 6px; }
  .gl-quick-btn { padding: 6px 10px; border-radius: 10px; font-size: 11px; border: 1px solid rgba(255,255,255,0.08); background: rgba(30,41,59,0.5); color: #cbd5e1; cursor: pointer; }
  .gl-panel-hdr { font-size: 13px; font-weight: 700; margin: 10px 0 8px; display: flex; gap: 8px; align-items: center; }
  .gl-panel-hdr span { font-size: 11px; color: #94a3b8; font-weight: 400; }
  .gl-goal-card { background: rgba(17,24,39,0.7); border: 1px solid rgba(255,255,255,0.05); border-left: 3px solid var(--card-color, #8b5cf6); border-radius: 14px; padding: 14px; margin-bottom: 10px; cursor: pointer; }
  .gl-goal-head { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
  .gl-goal-icon { font-size: 22px; }
  .gl-goal-info { flex: 1; min-width: 0; }
  .gl-goal-title { font-size: 14px; font-weight: 700; }
  .gl-goal-meta { display: flex; gap: 6px; margin-top: 4px; flex-wrap: wrap; align-items: center; }
  .gl-tf-chip { font-size: 9px; padding: 2px 7px; border-radius: 999px; border: 1px solid; }
  .gl-tf-chip.lg { font-size: 11px; padding: 4px 10px; }
  .gl-badge { font-size: 9px; padding: 2px 6px; border-radius: 999px; background: rgba(255,255,255,0.08); }
  .gl-badge.high { color: #f87171; } .gl-badge.medium { color: #fbbf24; } .gl-badge.low { color: #34d399; }
  .gl-review-tag { font-size: 9px; color: #fbbf24; }
  .gl-goal-pct { font-weight: 800; color: #34d399; }
  .gl-goal-bar-track { height: 5px; background: rgba(30,41,59,0.8); border-radius: 3px; overflow: hidden; }
  .gl-goal-bar-track.lg { height: 8px; margin-bottom: 16px; }
  .gl-goal-bar-fill { height: 100%; background: linear-gradient(90deg, #10b981, #34d399); border-radius: 3px; }
  .gl-sub-strip { margin-top: 10px; font-size: 11px; color: #93c5fd; display: flex; align-items: center; gap: 8px; }
  .gl-sub-mini i { font-style: normal; margin-left: 2px; }
  .gl-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.75); z-index: 200; display: flex; align-items: flex-end; }
  .gl-modal { width: 100%; max-height: 85vh; overflow-y: auto; background: #1e293b; border-radius: 16px 16px 0 0; padding: 20px; display: flex; flex-direction: column; gap: 10px; }
  .gl-modal input, .gl-modal textarea, .gl-modal select { padding: 10px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1); background: #0f172a; color: #fff; }
  .gl-submit { padding: 12px; border: none; border-radius: 10px; background: linear-gradient(135deg, #8b5cf6, #6366f1); color: #fff; font-weight: 700; cursor: pointer; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const detailStyles = `
  .gl-bc { font-size: 11px; color: #94a3b8; margin-bottom: 12px; display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }
  .gl-bc span::after { content: '›'; margin-left: 6px; opacity: 0.5; }
  .gl-bc .gl-bc-cur { color: #e2e8f0; font-weight: 600; }
  .gl-bc .gl-bc-cur::after { content: ''; }
  .gl-detail-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
  .gl-detail-pct { font-size: 32px; font-weight: 900; }
  .gl-desc { font-size: 13px; color: #cbd5e1; line-height: 1.5; margin: 8px 0 12px; }
  .gl-plan-meta { display: flex; gap: 14px; font-size: 12px; color: #94a3b8; margin-bottom: 12px; flex-wrap: wrap; }
  .gl-sub-h { font-size: 13px; margin: 14px 0 8px; color: #e2e8f0; }
  /* PDCA stepper */
  .gl-pdca-stepper { display: flex; gap: 6px; margin: 4px 0 18px; }
  .gl-pdca-step { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 10px 4px; border-radius: 12px; border: 1px solid rgba(255,255,255,0.06); background: rgba(17,24,39,0.6); color: #94a3b8; cursor: pointer; position: relative; }
  .gl-pdca-step.passed { border-color: rgba(52,211,153,0.3); }
  .gl-pdca-step.active { background: rgba(139,92,246,0.18); border-color: rgba(139,92,246,0.5); color: #f8fafc; }
  .gl-pdca-dot { font-size: 20px; }
  .gl-pdca-lbl { font-size: 12px; font-weight: 800; display: flex; flex-direction: column; align-items: center; line-height: 1.2; }
  .gl-pdca-lbl em { font-style: normal; font-size: 9px; font-weight: 500; opacity: 0.7; }
  .gl-pdca-panel { background: rgba(17,24,39,0.55); border: 1px solid rgba(255,255,255,0.05); border-radius: 16px; padding: 16px; }
  .dim-btn { width: 100%; padding: 10px; margin: 8px 0 12px; border-radius: 12px; border: none; background: linear-gradient(135deg, #f59e0b, #fbbf24); color: #0f172a; font-weight: 700; cursor: pointer; }
  .gl-ms { padding: 8px 0; font-size: 13px; cursor: pointer; border-bottom: 1px solid rgba(255,255,255,0.04); display: flex; justify-content: space-between; align-items: center; gap: 8px; }
  .gl-ms > span { flex: 1; }
  .gl-ms-del { background: none; border: none; color: #64748b; cursor: pointer; font-size: 12px; }
  .gl-ms-add { display: flex; gap: 8px; margin-top: 8px; }
  .gl-ms-add input { flex: 1; padding: 8px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1); background: #0f172a; color: #fff; }
  .gl-ms-add button { width: 40px; border-radius: 8px; border: none; background: rgba(139,92,246,0.3); color: #fff; cursor: pointer; }
  .gl-slider-lbl { display: block; font-size: 12px; color: #94a3b8; margin: 14px 0 6px; }
  .gl-pdca-panel input[type=range] { width: 100%; }
  .gl-step-next { width: 100%; margin-top: 16px; padding: 11px; border-radius: 10px; border: 1px solid rgba(139,92,246,0.4); background: rgba(139,92,246,0.15); color: #c4b5fd; font-weight: 700; cursor: pointer; }
  .gl-timeline { margin-top: 12px; display: flex; flex-direction: column; gap: 10px; }
  .gl-tl-item { border-left: 2px solid rgba(139,92,246,0.4); padding: 4px 0 4px 12px; }
  .gl-tl-head { display: flex; justify-content: space-between; font-size: 12px; margin-bottom: 4px; }
  .gl-tl-head b { color: #34d399; } .gl-tl-head span { color: #64748b; }
  .gl-tl-item p { font-size: 12px; color: #cbd5e1; margin: 3px 0; line-height: 1.45; }
  .gl-act-note { background: rgba(96,165,250,0.1); border: 1px solid rgba(96,165,250,0.25); border-radius: 12px; padding: 12px; font-size: 12px; color: #bfdbfe; line-height: 1.6; margin-bottom: 14px; }
  .gl-act-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .gl-act-grid button { padding: 12px 8px; border-radius: 10px; border: none; cursor: pointer; font-weight: 600; font-size: 12px; background: rgba(30,41,59,0.8); color: #cbd5e1; }
  .gl-act-grid button.ok { background: #22c55e; color: #fff; }
  .gl-del-btn { width: 100%; margin-top: 14px; padding: 10px; border-radius: 10px; border: none; cursor: pointer; background: rgba(239,68,68,0.15); color: #f87171; font-weight: 600; }
  .gl-modal-tall label { font-size: 11px; color: #94a3b8; }
`;

export default LifeGoalsCenter;
